import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import ComicCard from '@/pages/home/components/ComicCard';
import { categories, allComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

type SortKey = 'latest' | 'popular' | 'rating';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const currentCategory = categories.find((c) => c.slug === currentSlug);

  const filteredComics: Comic[] = useMemo(() => {
    let result = currentSlug === 'all'
      ? [...allComics]
      : [...allComics.filter((c) => c.categories.includes(currentSlug))];

    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [currentSlug, sortBy]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const pagedComics = filteredComics.slice((page - 1) * pageSize, page * pageSize);

  const handleCategoryChange = (newSlug: string) => {
    setPage(1);
  };

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'latest', label: '最近更新' },
    { key: 'popular', label: '最受欢迎' },
    { key: 'rating', label: '评分最高' },
  ];

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: currentCategory?.name || '全部漫画' },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebSite" />
      <SEOSchema type="WebPage" data={{
        name: `${currentCategory?.name || '全部漫画'} - 虫虫动漫免费漫画分类`,
        description: currentCategory?.description || '浏览虫虫动漫全部免费漫画作品',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-4 mb-6">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                {currentCategory?.name || '全部漫画'}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                {currentCategory?.description || '浏览虫虫动漫全部免费漫画作品'}
                {currentCategory && ` - 共 ${currentCategory.count} 部作品`}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              <aside className="lg:w-56 flex-shrink-0">
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 lg:sticky lg:top-20">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">漫画分类</h3>
                  <div className="flex flex-wrap lg:flex-col gap-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        onClick={() => handleCategoryChange(cat.slug)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${
                          currentSlug === cat.slug
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        <i className={`${cat.icon} text-xs`}></i>
                        <span>{cat.name}</span>
                        <span className={`text-xs ml-auto ${
                          currentSlug === cat.slug ? 'text-background-50/70' : 'text-foreground-400'
                        }`}>
                          {cat.count}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-700">{filteredComics.length}</strong> 部作品
                  </p>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                    {sortOptions.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => { setSortBy(opt.key); setPage(1); }}
                        className={`px-3 py-1.5 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap ${
                          sortBy === opt.key
                            ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                            : 'text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {pagedComics.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-product-shop="">
                      {pagedComics.map((comic) => (
                        <ComicCard
                          key={comic.id}
                          id={comic.id}
                          title={comic.title}
                          slug={comic.slug}
                          cover={comic.cover}
                          author={comic.author}
                          category={comic.category}
                          status={comic.status}
                          latestChapter={comic.latestChapter}
                          rating={comic.rating}
                          views={comic.views}
                          shortDescription={comic.shortDescription}
                        />
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-8">
                        <button
                          onClick={() => setPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="w-8 h-8 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                          aria-label="上一页"
                        >
                          <i className="ri-arrow-left-s-line"></i>
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition-colors cursor-pointer ${
                              p === page
                                ? 'bg-primary-500 text-background-50'
                                : 'text-foreground-500 hover:bg-background-100'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          onClick={() => setPage(Math.min(totalPages, page + 1))}
                          disabled={page === totalPages}
                          className="w-8 h-8 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                          aria-label="下一页"
                        >
                          <i className="ri-arrow-right-s-line"></i>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                      <i className="ri-emotion-sad-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-500">该分类下暂无漫画作品</p>
                    <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block">
                      返回首页
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}